import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  clearReferralCode,
  deviceId,
  storedReferralCode,
} from '@/features/referrals/referral-code';
import { onboardUser } from '@/services/onboarding';

/**
 * Приём пользователя сразу после входа.
 *
 * Профиль и стартовые кредиты заводит сервер. Раньше это происходило при
 * первой генерации — и получался тупик: мастер не даёт создать портрет
 * при нулевом балансе, а до первой генерации баланс и был нулевым.
 *
 * Код приглашения доносится именно здесь: onboard_user создаёт реферала
 * только вместе с профилем, и после приёма код уже ничего не решает.
 */
export function useEnsureOnboarded(): void {
  const { status, session } = useAuthSession();
  const queryClient = useQueryClient();

  // Одна попытка на сессию: повтор безвреден (функция идемпотентна), но
  // лишний запрос на каждый рендер не нужен.
  const onboarded = useRef<string | null>(null);

  useEffect(() => {
    const userId = session?.user.id;

    if (status !== 'ready' || !session || !userId || onboarded.current === userId) {
      return;
    }

    onboarded.current = userId;
    const referralCode = storedReferralCode();

    onboardUser(
      {
        deviceHash: deviceId(),
        ...(referralCode ? { referralCode } : {}),
      },
      session.access_token,
    )
      .then(() => {
        // Реферал заведён (или отклонён) сервером — код больше не нужен.
        clearReferralCode();
        // Профиль появился — с ним появились баланс и код приглашения.
        void queryClient.invalidateQueries({ queryKey: ['credits'] });
        void queryClient.invalidateQueries({ queryKey: ['referrals'] });
      })
      .catch(() => {
        /*
         * Молча: приём повторится при следующей загрузке, а генерация
         * заводит профиль сама. Показывать человеку ошибку, которую он не
         * может исправить, смысла нет — но и код приглашения не стираем.
         */
        onboarded.current = null;
      });
  }, [queryClient, session, status]);
}
